import React, { useEffect, useMemo, useState } from "react";
import { ReportTab } from "./ReportTab";
// After ReportTab on purpose: the shared dropdown's import chain runs through
// the utils barrel to the route table and back into itself. ReportTab loads
// utils first, so the dropdown is fully built by the time the pages need it.
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { CollabTrendChart } from "./CollabTrendChart";
import { ProficiencyTrendChart } from "./ProficiencyTrendChart";
import { schoolYearLabel } from "./TrendBrickChart";
import {
  downloadDosageTrendCSV,
  downloadStudentCollabTrendCSV,
  downloadStudentProficiencyCSV,
  downloadTeacherSummaryCSV,
  fetchDosageTrend,
  fetchStudentCollabTrend,
  fetchStudentProficiency,
  fetchTeacherSummary,
} from "./schoolReportsApi";

const wholeNumber = (v) =>
  v === null || v === undefined ? "" : Math.round(v).toLocaleString();
const oneDecimal = (v) =>
  v === null || v === undefined ? "" : Number(v).toFixed(1);
const percent = (v) => (v === null || v === undefined ? "" : `${v}%`);
// Missing is not zero: an entity with no scored CONSENSUS session reads as a
// dash, never 0.0. Scoped to the collaboration averages, which are the only
// columns the contract calls out as routinely null.
const EM_DASH = "\u2014";
const scoreOrDash = (v) =>
  v === null || v === undefined ? EM_DASH : Number(v).toFixed(1);

// Sub-standards are stored fused and lowercase ("3.NF.A.3b"); display them
// dotted ("3.NF.A.3.B"). The whole code is uppercased at the end so shapes
// the split doesn't recognise, like the hyphenated "hsa-rei.b.3", read the
// same way.
export const formatStandardCode = (code) => {
  if (!code) return "";
  return String(code)
    .split(".")
    .flatMap((part) => {
      const fused = part.match(/^(\d+)([a-zA-Z]+)$/);
      if (fused) return [fused[1], fused[2]];
      return [part];
    })
    .join(".")
    .toUpperCase();
};

// Column keys match the server's CSV column lists so the table and the CSV
// download always agree. Row order comes from the server. The column set
// depends on View By (the grouped entity) and Trend By (the period).

const PERIOD_HEADERS = { week: "Week Of", month: "Month Of", school_year: "School Year" };

const periodColumn = (trendBy) =>
  trendBy === "school_year"
    ? ["period_of", PERIOD_HEADERS.school_year, { formatter: schoolYearLabel }]
    : ["period_of", PERIOD_HEADERS[trendBy] || PERIOD_HEADERS.week];

// Every report's rows carry exactly the columns of the chosen view, in the
// order the contract lists. One dictionary names and formats each key; each
// tab lists which keys its views carry. `name` is always the grouped entity.
const REPORT_FIELDS = {
  school_name: ["School"],
  teacher_email: ["Teacher Email"],
  teacher_name: ["Teacher Name"],
  name: ["Name"],
  active_teachers: ["Active Teachers"],
  session_count: ["Session Count"],
  active_students: ["Active Students"],
  student_duration_mins: ["Total Student Session Time (Min)", { formatter: wholeNumber }],
  avg_students_per_session: ["Avg Students Per Session", { formatter: oneDecimal }],
  // The server measures this differently per view: minutes / active students
  // at teacher and school grain, minutes / sessions at student grain. It sends
  // one CSV header for all three, so the table uses one too rather than
  // labelling the screen and the download differently.
  avg_student_duration_mins: ["Avg Student Session Time (Min)", { formatter: oneDecimal }],
  avg_group_collaboration_score: [
    "Avg Group Collaboration Score",
    { formatter: scoreOrDash },
  ],
  avg_student_collaboration_score: [
    "Avg Student Collaboration Score",
    { formatter: scoreOrDash },
  ],
  active_students_all_sy: ["Active Students (All SY)"],
  // The server's wording when no trend is sent; Dosage overrides it per trend.
  active_students_this_month: ["Active Students (This Month)"],
  students_gt_3_sessions: ["Students: >3 Sessions"],
  students_gt_7_sessions: ["Students: >7 Sessions"],
  last_session_date: ["Last Session"],
  avg_collaboration_score: ["Avg Student Collaboration Score", { formatter: scoreOrDash }],
  level_4_session_count: ["Level 4 Session Count"],
  level_3_session_count: ["Level 3 Session Count"],
  level_2_session_count: ["Level 2 Session Count"],
  level_1_session_count: ["Level 1 Session Count"],
  standard_code: ["Standard Code", { formatter: formatStandardCode }],
  standard_name: ["Standard Name"],
  level: ["Level"],
  questions_answered: ["Question Count"],
  correct_solo: ["Correct - Solo"],
  accuracy_rate: ["Accuracy Rate (%)", { formatter: percent }],
  last_5_accuracy: ["Last 5 Accuracy (%)", { formatter: percent }],
  cumulative_questions: ["Cumulative Questions"],
  cumulative_correct: ["Cumulative Correct"],
  last_question_date: ["Last Question"],
  level_1_count: ["Level 1 Count"],
  level_2_count: ["Level 2 Count"],
  level_3_count: ["Level 3 Count"],
  wip_count: ["WIP Count"],
};

// The headers named for the view rather than the field.
const HEADER_BY_VIEW = {
  name: { teacher: "Teacher Name", school: "School", student: "Student Name" },
  avg_student_collaboration_score: { student: "Avg Collaboration Score" },
  avg_collaboration_score: { student: "Avg Collaboration Score" },
};

// `overrides` carries the headers a caller can only know at call time, such
// as the ones that follow Trend By rather than the grouping.
const columnsForView = (viewBy, keysByView, overrides = {}) => {
  const view = keysByView[viewBy] ? viewBy : "teacher";
  return keysByView[view].map((key) => {
    const [header, meta] = REPORT_FIELDS[key];
    return [key, overrides[key] || HEADER_BY_VIEW[key]?.[view] || header, meta];
  });
};

const SUMMARY_KEYS = {
  teacher: [
    "school_name",
    "teacher_email",
    "name",
    "session_count",
    "active_students",
    "student_duration_mins",
    "avg_students_per_session",
    "avg_student_duration_mins",
    "avg_group_collaboration_score",
    "avg_student_collaboration_score",
    "last_session_date",
  ],
  school: [
    "name",
    "active_teachers",
    "session_count",
    "active_students",
    "student_duration_mins",
    "avg_students_per_session",
    "avg_student_duration_mins",
    "avg_group_collaboration_score",
    "avg_student_collaboration_score",
    "last_session_date",
  ],
  student: [
    "school_name",
    "teacher_email",
    "name",
    "session_count",
    "student_duration_mins",
    "avg_student_duration_mins",
    "avg_student_collaboration_score",
  ],
};

// The period column leads every Dosage view, so it's prefixed rather than
// listed: its key and header depend on Trend By, not on the grouping.
const DOSAGE_KEYS = {
  teacher: [
    "teacher_email",
    "name",
    "school_name",
    "session_count",
    "student_duration_mins",
    "avg_students_per_session",
    "avg_student_duration_mins",
    "active_students_all_sy",
    "active_students_this_month",
    "students_gt_3_sessions",
    "students_gt_7_sessions",
  ],
  school: [
    "name",
    "active_teachers",
    "session_count",
    "student_duration_mins",
    "avg_students_per_session",
    "avg_student_duration_mins",
    "active_students_all_sy",
    "active_students_this_month",
    "students_gt_3_sessions",
    "students_gt_7_sessions",
  ],
  student: [
    "teacher_email",
    "teacher_name",
    "school_name",
    "name",
    "session_count",
    "student_duration_mins",
    "avg_student_duration_mins",
  ],
};

export const summaryColumns = (viewBy) => columnsForView(viewBy, SUMMARY_KEYS);

export const dosageColumns = (viewBy, trendBy) => [
  periodColumn(trendBy),
  ...columnsForView(viewBy, DOSAGE_KEYS, {
    // Like the period column, this header names the chosen period when there
    // is one. The key keeps its old name either way.
    active_students_this_month: trendBy
      ? "Active Students (Period)"
      : "Active Students (This Month)",
  }),
];

const COLLAB_LEVELS = [
  "avg_collaboration_score",
  "level_4_session_count",
  "level_3_session_count",
  "level_2_session_count",
  "level_1_session_count",
];

// At teacher and school grain a level count is a count of sessions; at
// student grain it is that student's session records. Same header either way.
// The group score exists only where there is a group to average: a student
// row has just the student's own score.
// Column order follows the design doc, which puts duration ahead of the
// session and student counts on this tab (the server's key order differs).
const COLLAB_KEYS = {
  teacher: [
    "school_name",
    "teacher_email",
    "name",
    "student_duration_mins",
    "session_count",
    "active_students",
    "avg_group_collaboration_score",
    ...COLLAB_LEVELS,
  ],
  school: [
    "name",
    "active_teachers",
    "student_duration_mins",
    "session_count",
    "active_students",
    "avg_group_collaboration_score",
    ...COLLAB_LEVELS,
  ],
  student: [
    "school_name",
    "teacher_email",
    "name",
    "student_duration_mins",
    "session_count",
    ...COLLAB_LEVELS,
  ],
};

export const collaborationColumns = (viewBy, trendBy) => [
  periodColumn(trendBy),
  ...columnsForView(viewBy, COLLAB_KEYS),
];

// Proficiency is three different tables. Only the Student view lists standards
// per student; the Teacher and School views are rollups of level counts.
const PROFICIENCY_LEVELS = ["level_1_count", "level_2_count", "level_3_count", "wip_count"];

const PROFICIENCY_KEYS = {
  student: [
    "school_name",
    "teacher_email",
    "name",
    "standard_code",
    "standard_name",
    "questions_answered",
    "correct_solo",
    "accuracy_rate",
    "level",
    "last_5_accuracy",
    "cumulative_questions",
    "cumulative_correct",
    "last_question_date",
  ],
  teacher: ["school_name", "teacher_email", "name", "active_students", ...PROFICIENCY_LEVELS],
  school: [
    "name",
    "standard_code",
    "standard_name",
    "active_teachers",
    "active_students",
    ...PROFICIENCY_LEVELS,
  ],
};

const PERIOD_BAND = { week: "Weekly", month: "Monthly", school_year: "Yearly" };

// The Student view's two header bands: the row's own period, then everything
// from the report's start date through that period. The CSV names the same
// columns "Period X" / "Cumulative X".
const STUDENT_BANDS = {
  questions_answered: [
    "period",
    "Questions",
    "The count of questions this student worked on for this standard during this row’s period.",
  ],
  correct_solo: [
    "period",
    "Correct",
    "The count of questions answered correctly during Solo Time in this row’s period. Team Time answers are not counted.",
  ],
  accuracy_rate: [
    "period",
    "Accuracy",
    "Solo Correct ÷ Questions for this row’s period. Example: 3 of 5 = 60%.",
  ],
  level: [
    "cumulative",
    "Level",
    "Based on the student’s last 5 questions on this standard, as of the end of this row’s period. WIP = fewer than 5 questions worked on.",
  ],
  last_5_accuracy: [
    "cumulative",
    "Last 5",
    "Solo accuracy on the student’s 5 most recent questions (or fewer) on this standard, as of the end of this row’s period.",
  ],
  cumulative_questions: [
    "cumulative",
    "Questions",
    "All questions this student has worked on for this standard, as of the end of this row’s period.",
  ],
  cumulative_correct: [
    "cumulative",
    "Correct",
    "All questions answered correctly during Solo Time on this standard, as of the end of this row’s period.",
  ],
};

export const proficiencyColumns = (viewBy, trendBy) => {
  const columns = [periodColumn(trendBy), ...columnsForView(viewBy, PROFICIENCY_KEYS)];
  if (viewBy !== "student") return columns;
  const bands = { period: PERIOD_BAND[trendBy] || PERIOD_BAND.week, cumulative: "Cumulative" };
  return columns.map(([key, header, meta]) => {
    if (!STUDENT_BANDS[key]) return [key, header, meta];
    const [band, label, tip] = STUDENT_BANDS[key];
    return [key, label, { ...meta, band: bands[band], tip }];
  });
};

// The graph sums the level counts per period; a school-year period is one
// bar, and the Student view's rows carry no counts to sum.
export const showProficiencyChart = (viewBy, trendBy) =>
  viewBy !== "student" && trendBy !== "school_year";

// The response's domain codes are grade-qualified ("5.NF"); the filter sends
// the grade-agnostic short code ("NF"), as the class report does. Only K-8
// codes shorten: a high-school code's second segment can be a cluster letter
// ("hsa-rei.b"), so it keeps its full code and the server matches it exactly.
const gradeOf = (code) => String(code || "").split(".")[0];
export const shortOf = (code) =>
  /^(K|\d+)$/i.test(gradeOf(code))
    ? String(code).split(".").slice(1).join(".")
    : String(code || "");

// One option per short code, labelled by the first one in the response's order.
export const domainOptions = (domains) => {
  const byCode = new Map();
  (domains || []).forEach((d) => {
    const code = shortOf(d.code);
    if (code && !byCode.has(code)) byCode.set(code, d.name);
  });
  return [...byCode]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, name]) => ({ code, name }));
};

/** Proficiency's Domain filter, over options already narrowed by the grade. */
const DomainSelect = ({ domains, value, onChange }) => {
  const options = [
    { value: null, label: "All Domains" },
    ...domains.map((d) => ({
      value: d.code,
      label: d.name ? `${d.code} ${d.name}` : d.code,
    })),
  ];
  return (
    <SelectDropdown
      label="Domain"
      value={value}
      displayText={options.find((o) => o.value === value)?.label || "All Domains"}
      options={options}
      onChange={onChange}
      clearable
    />
  );
};

const gradeRank = (grade) => {
  if (grade === "K") return -1;
  return /^\d+$/.test(grade) ? Number(grade) : 99;
};

const gradeLabel = (grade) => {
  if (grade === "K") return "Kindergarten";
  if (!/^\d+$/.test(grade)) return grade;
  const n = Number(grade);
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return `${n}${suffix} Grade`;
};

// K, then numeric grades, then high-school codes alphabetically.
export const gradeOptions = (domains) =>
  [...new Set((domains || []).map((d) => gradeOf(d.code)).filter(Boolean))]
    .sort((a, b) => gradeRank(a) - gradeRank(b) || a.localeCompare(b))
    .map((grade) => ({ value: grade, label: gradeLabel(grade) }));

/**
 * Proficiency's Grade and Domain filters. The last non-empty domain list is
 * kept so neither control blanks out between a selection and the refetch
 * it triggers. A selection that left the range (date or scope changed) is
 * dropped so the rows and the controls agree.
 */
const ProficiencyFilters = ({ domains, grade, onGradeChange, domain, onDomainChange }) => {
  const [known, setKnown] = useState([]);
  useEffect(() => {
    if (domains && domains.length > 0) setKnown(domains);
  }, [domains]);

  // Each control narrows the other: a grade lists only its domains, a domain
  // only the grades that have it.
  const grades = useMemo(
    () => gradeOptions(domain ? known.filter((d) => shortOf(d.code) === domain) : known),
    [known, domain]
  );
  const gradeDomains = useMemo(
    () => domainOptions(grade ? known.filter((d) => gradeOf(d.code) === grade) : known),
    [known, grade]
  );
  // Checked against the whole list, not the narrowed ones: a stale pair can
  // empty both narrowed lists, which would leave nothing to compare against.
  useEffect(() => {
    if (known.length === 0) return;
    const has = (g, d) =>
      known.some(
        (c) => (!g || gradeOf(c.code) === g) && (!d || shortOf(c.code) === d)
      );
    const gradeStays = !grade || has(grade, null);
    if (!gradeStays) onGradeChange(null);
    if (domain && !has(gradeStays ? grade : null, domain)) onDomainChange(null);
  }, [known, grade, domain, onGradeChange, onDomainChange]);

  const options = [{ value: null, label: "All Grades" }, ...grades];
  return (
    <>
      <SelectDropdown
        label="Grade"
        value={grade}
        displayText={options.find((o) => o.value === grade)?.label || "All Grades"}
        options={options}
        onChange={onGradeChange}
        clearable
      />
      <DomainSelect
        domains={gradeDomains}
        value={domain}
        onChange={onDomainChange}
      />
    </>
  );
};

export const TeacherSummaryTab = ({ params, tabName, reportScope, viewBy }) => (
  <ReportTab
    params={params}
    tabName={tabName}
    reportScope={reportScope}
    fetchJson={fetchTeacherSummary}
    fetchCsv={downloadTeacherSummaryCSV}
    errorTag="school-reports-teacher-summary"
    columns={summaryColumns(viewBy)}
  />
);

export const DosageTrendTab = ({ params, tabName, reportScope, viewBy, trendBy }) => (
  <ReportTab
    params={params}
    tabName={tabName}
    reportScope={reportScope}
    fetchJson={fetchDosageTrend}
    fetchCsv={downloadDosageTrendCSV}
    errorTag="school-reports-dosage-trend"
    columns={dosageColumns(viewBy, trendBy)}
  />
);

export const StudentCollabTrendTab = ({ params, tabName, reportScope, viewBy, trendBy }) => (
  <ReportTab
    params={params}
    tabName={tabName}
    reportScope={reportScope}
    fetchJson={fetchStudentCollabTrend}
    fetchCsv={downloadStudentCollabTrendCSV}
    errorTag="school-reports-student-collab-trend"
    columns={collaborationColumns(viewBy, trendBy)}
    chart={(rows) => <CollabTrendChart rows={rows} trendBy={trendBy} />}
  />
);

export const StudentProficiencyTab = ({ params, tabName, reportScope, viewBy, trendBy }) => {
  const [grade, setGrade] = useState(null);
  const [domain, setDomain] = useState(null);
  // Grade and domain ride on both the JSON fetch and the CSV download.
  const tabParams = useMemo(
    () =>
      params
        ? { ...params, grade: grade ?? undefined, domain: domain ?? undefined }
        : params,
    [params, grade, domain]
  );
  return (
    <ReportTab
      params={tabParams}
      tabName={tabName}
      reportScope={reportScope}
      fetchJson={fetchStudentProficiency}
      fetchCsv={downloadStudentProficiencyCSV}
      errorTag="school-reports-student-proficiency"
      columns={proficiencyColumns(viewBy, trendBy)}
      toolbar={(data) => (
        <ProficiencyFilters
          domains={data?.domains}
          grade={grade}
          onGradeChange={setGrade}
          domain={domain}
          onDomainChange={setDomain}
        />
      )}
      chart={
        showProficiencyChart(viewBy, trendBy)
          ? (rows) => <ProficiencyTrendChart rows={rows} trendBy={trendBy} />
          : undefined
      }
    />
  );
};
